import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi } from '../../api';
import { onDoctorBookingConfirmed } from './bookingUtils';
import { useProviderSlots, preferredTimeGrid, formatSlotTime } from './useProviderSlots';
import type { MarketplaceProvider, ProviderPricing } from '../../types';

// ─── Inline SVG Icons ────────────────────────────────────────────────────────

function IconBack({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function IconCheck({ color, size = 18 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function IconCalendar({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M8 2v4M16 2v4M3 10h18" />
    </svg>
  );
}

function IconClock({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function IconBuilding({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M5 21V7l7-4 7 4v14" />
      <rect x="9" y="10" width="6" height="6" />
    </svg>
  );
}

function IconVideo({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 7l-7 5 7 5V7z" />
      <rect x="1" y="5" width="15" height="14" rx="2" />
    </svg>
  );
}

function IconRepeat({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}

function IconShield({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
    </svg>
  );
}

function IconCash({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="3" />
      <path d="M6 12h.01M18 12h.01" />
    </svg>
  );
}

function IconCard({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <path d="M1 10h22" />
    </svg>
  );
}

function IconStethoscope({ color }: { color: string }) {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
      <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
      <circle cx="20" cy="10" r="2" />
    </svg>
  );
}

// ─── Loading Spinner ──────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div
        style={{
          width: 40,
          height: 40,
          border: `3px solid ${T.border}`,
          borderTop: `3px solid ${T.teal}`,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function getDateChipLabel(offset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  if (offset === 0) return 'Today';
  if (offset === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}

function getFullDate(offset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().split('T')[0];
}

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 0, background: T.white, borderBottom: `1px solid ${T.border}` }}>
      {Array.from({ length: total }, (_, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {/* Left connector */}
            {i > 0 && (
              <div style={{ flex: 1, height: 2, background: i < current ? T.teal : T.border, transition: 'background 0.3s ease' }} />
            )}
            {/* Circle */}
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: i + 1 < current ? T.teal : i + 1 === current ? T.navy : T.border,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'background 0.3s ease',
              }}
            >
              {i + 1 < current ? (
                <IconCheck color={T.white} size={12} />
              ) : (
                <span style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: i + 1 <= current ? T.white : T.textTer }}>
                  {i + 1}
                </span>
              )}
            </div>
            {/* Right connector */}
            {i < total - 1 && (
              <div style={{ flex: 1, height: 2, background: i + 1 < current ? T.teal : T.border, transition: 'background 0.3s ease' }} />
            )}
          </div>
          <span style={{ fontFamily: T.font, fontSize: 9, fontWeight: i + 1 === current ? 700 : 500, color: i + 1 === current ? T.navy : T.textTer, textAlign: 'center', whiteSpace: 'nowrap' }}>
            {['Service', 'Date & Time', 'Details', 'Confirm'][i]}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Service Option Card ──────────────────────────────────────────────────────

function ServiceOption({
  icon,
  label,
  description,
  price,
  selected,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  price?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        background: selected ? T.navy + '06' : T.white,
        border: `2px solid ${selected ? T.navy : T.border}`,
        borderRadius: 14,
        padding: '14px 16px',
        cursor: 'pointer',
        textAlign: 'left',
        marginBottom: 10,
        transition: 'all 0.15s ease',
        boxShadow: selected ? `0 0 0 3px ${T.navy}18` : T.shadow,
      }}
    >
      <div style={{ width: 44, height: 44, borderRadius: 12, background: selected ? T.navy + '12' : T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s ease' }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.text }}>{label}</div>
        <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, marginTop: 2 }}>{description}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {price && <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.navy }}>{price}</div>}
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            border: `2px solid ${selected ? T.navy : T.border}`,
            background: selected ? T.navy : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: price ? 4 : 0,
            marginLeft: 'auto',
            transition: 'all 0.15s ease',
          }}
        >
          {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.white }} />}
        </div>
      </div>
    </button>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────

function SuccessScreen({
  doctorName,
  tokenNumber,
  date,
  time,
  serviceType,
  consentWarning,
  onViewAppointments,
  onBackToServices,
  onGoToTimeline,
}: {
  doctorName: string;
  tokenNumber?: string;
  date: string;
  time: string;
  serviceType: string;
  consentWarning?: string;
  onViewAppointments: () => void;
  onBackToServices: () => void;
  onGoToTimeline: () => void;
}) {
  const nav = useNavigate();
  return (
    <div style={{ background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 24px', fontFamily: T.font, paddingBottom: 100 }}>
      {/* Green checkmark circle */}
      <div
        style={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          background: `linear-gradient(135deg, #10B981 0%, #059669 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 24,
          boxShadow: '0 8px 24px rgba(16,185,129,0.3)',
        }}
      >
        <IconCheck color={T.white} size={42} />
      </div>

      <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: '0 0 6px', textAlign: 'center' }}>
        Appointment Requested
      </h2>
      <p style={{ fontSize: 13, color: T.textSec, margin: '0 0 28px', textAlign: 'center', lineHeight: 1.6 }}>
        Your booking has been recorded — the clinic will confirm your slot.
      </p>

      {consentWarning && (
        <div style={{ width: '100%', maxWidth: 360, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
          <span style={{ fontFamily: T.font, fontSize: 11, color: '#92400E', fontWeight: 600 }}>{consentWarning}</span>
        </div>
      )}

      {/* Booking card */}
      <div
        style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: 18,
          padding: '20px',
          width: '100%',
          maxWidth: 360,
          boxShadow: T.shadowMd,
          marginBottom: 28,
        }}
      >
        {/* Doctor name */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${T.border}` }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: T.navy + '10', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconStethoscope color={T.navy} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{doctorName}</div>
            <div style={{ fontSize: 12, color: T.textSec, marginTop: 2 }}>{serviceType}</div>
          </div>
        </div>

        {/* Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: T.textSec, display: 'flex', alignItems: 'center', gap: 5 }}>
              <IconCalendar color={T.textTer} /> Date
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{date}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: T.textSec, display: 'flex', alignItems: 'center', gap: 5 }}>
              <IconClock color={T.textTer} /> Time
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{time}</span>
          </div>
          {tokenNumber && (
            <div
              style={{
                marginTop: 8,
                background: T.teal + '10',
                border: `1px solid ${T.teal}30`,
                borderRadius: 10,
                padding: '10px 14px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 12, color: T.textSec }}>Token Number</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: T.teal }}>{tokenNumber}</span>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 360 }}>
        <button
          onClick={onViewAppointments}
          style={{
            fontFamily: T.font,
            fontSize: 14,
            fontWeight: 700,
            color: T.white,
            background: `linear-gradient(135deg, ${T.teal} 0%, #009B9B 100%)`,
            border: 'none',
            borderRadius: 12,
            padding: '14px 0',
            cursor: 'pointer',
            boxShadow: `0 3px 12px ${T.teal}35`,
          }}
        >
          View Appointments
        </button>
        <button
          onClick={onGoToTimeline}
          style={{
            fontFamily: T.font,
            fontSize: 14,
            fontWeight: 600,
            color: T.navy,
            background: T.white,
            border: `1.5px solid ${T.navy}30`,
            borderRadius: 12,
            padding: '13px 0',
            cursor: 'pointer',
          }}
        >
          Go to Timeline
        </button>
        <button
          onClick={onBackToServices}
          style={{
            fontFamily: T.font,
            fontSize: 14,
            fontWeight: 600,
            color: T.textSec,
            background: T.white,
            border: `1.5px solid ${T.border}`,
            borderRadius: 12,
            padding: '13px 0',
            cursor: 'pointer',
          }}
        >
          Back to Care Services
        </button>
      </div>

      {/* NHC trust note */}
      <div style={{ width: '100%', maxWidth: 360, marginTop: 16, textAlign: 'center' }}>
        <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
          NeoHealthCard™ · Booking recorded on your NeoHealthCard account
        </span>
      </div>

      {/* Suggested follow-up */}
      <div style={{ width: '100%', maxWidth: 360, marginTop: 24 }}>
        <div style={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 10 }}>
          Suggested Follow-up
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div
            onClick={() => nav('/patient/care-services/labs')}
            style={{
              flex: 1,
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              padding: 14,
              cursor: 'pointer',
              textAlign: 'center',
              boxShadow: T.shadow,
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 6 }}>🧪</div>
            <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>Need Lab Tests?</div>
            <div style={{ fontFamily: T.font, fontSize: 11, color: T.teal, fontWeight: 600 }}>Book Lab Test</div>
          </div>
          <div
            onClick={() => nav('/patient/care-services/pharmacy')}
            style={{
              flex: 1,
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              padding: 14,
              cursor: 'pointer',
              textAlign: 'center',
              boxShadow: T.shadow,
            }}
          >
            <div style={{ fontSize: 24, marginBottom: 6 }}>💊</div>
            <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>Need Pharmacy?</div>
            <div style={{ fontFamily: T.font, fontSize: 11, color: '#059669', fontWeight: 600 }}>Order Medicine</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DoctorBooking() {
  const { doctorId } = useParams<{ doctorId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const preselectedType = searchParams.get('type');
  const preselectedDate = searchParams.get('date');
  const preselectedTime = searchParams.get('time');
  // A slot id arrives when the profile screen already picked a PUBLISHED slot.
  const preselectedSlotId = searchParams.get('slotId');

  const [doctor, setDoctor] = useState<MarketplaceProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState<{ tokenNumber?: string; appointmentId?: string }>({});

  // Step 1
  const [consultationType, setConsultationType] = useState<string>(
    preselectedType?.toLowerCase() === 'telemedicine' ? 'telemedicine' : 'clinic'
  );

  // Step 2 — compute initial date offset from preselected date
  const [selectedDateOffset, setSelectedDateOffset] = useState(() => {
    if (!preselectedDate) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(preselectedDate + 'T00:00:00');
    const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff >= 0 && diff <= 4 ? diff : 0;
  });
  const [selectedSlot, setSelectedSlot] = useState<string | null>(preselectedTime || null);
  /** Set only when the chosen time is a slot the doctor actually published. */
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(preselectedSlotId);

  // The doctor's REAL published openings. `hasSchedule: false` means this
  // doctor publishes none, and the screen then asks for a preferred time and
  // says so — it never presents unchecked times as availability.
  const slots = useProviderSlots(doctorId, 14);

  // Step 3
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [shareRecords, setShareRecords] = useState(false);

  // Step 4 — there is no online payment integration; fees are paid at the
  // clinic, and no selector should suggest otherwise.
  const [consentWarning, setConsentWarning] = useState('');

  useEffect(() => {
    let alive = true;
    if (!doctorId) return;
    patientApi
      .getMarketplaceProvider(doctorId)
      .then((res) => { if (alive) setDoctor(res); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [doctorId]);

  const pricing: ProviderPricing[] = (() => {
    if (!doctor?.pricing) return [];
    const p = typeof doctor.pricing === 'string' ? JSON.parse(doctor.pricing) : doctor.pricing;
    return Array.isArray(p) ? p : [];
  })();

  const getPriceForType = (type: string) => {
    const map: Record<string, string[]> = {
      clinic: ['clinic', 'consultation', 'visit', 'opd'],
      telemedicine: ['telemedicine', 'video', 'online', 'remote'],
      followup: ['follow-up', 'followup', 'follow up', 'revisit'],
    };
    const keywords = map[type] || [];
    const match = pricing.find((p) => keywords.some((k) => p.service.toLowerCase().includes(k)));
    return match ? `₹${match.price}` : pricing[0] ? `₹${pricing[0].price}` : undefined;
  };

  const consultTypeLabel: Record<string, string> = {
    clinic: 'Clinic Visit',
    telemedicine: 'Telemedicine',
    followup: 'Follow-up',
  };

  const handleSubmit = async () => {
    if (!doctor || !selectedSlot) return;
    setSubmitting(true);
    setSubmitError('');
    const dateStr = getFullDate(selectedDateOffset);
    const appointedAt = `${dateStr}T${selectedSlot}:00`;

    try {
      const result = await patientApi.bookDoctor({
        providerId: doctor.id,
        appointedAt,
        consultationType,
        // Booking a published slot takes that seat on the server.
        ...(selectedSlotId ? { slotId: selectedSlotId } : {}),
        // The additional note rides on the booking itself — otherwise the
        // doctor never sees it (the local mirror is patient-side only).
        reasonForVisit: note.trim() ? `${reason} · Note: ${note.trim()}` : reason,
      });
      setSuccessData({ tokenNumber: result?.tokenNumber, appointmentId: result?.appointmentId });

      onDoctorBookingConfirmed({
        id: result?.id,
        doctorName: doctor.name,
        specialty: doctor.specialties?.[0] || 'General',
        hospital: doctor.metadata?.hospitalAffiliation || doctor.city || 'Clinic',
        date: dateStr,
        time: selectedSlot,
        type: consultationType === 'telemedicine' ? 'Telemedicine' : 'In-Person',
        notes: note || undefined,
      });

      // The consent checkbox creates a REAL consent grant — without this the
      // checkbox was pure decoration while the copy promised audit logging.
      if (shareRecords) {
        /**
         * A consent binds to a recipient by their card id and nothing else, so
         * one recorded against a typed name — which this used to send — listed
         * as ACTIVE and opened nothing for anybody. A doctor with no card id
         * cannot be granted anything, and the patient is told so rather than
         * shown a grant that does nothing.
         */
        if (!doctor.neoHealthCardId) {
          setConsentWarning('Booking succeeded, but this doctor has no NeoHealthCard id yet, so records could not be shared with them.');
        } else {
          try {
            await patientApi.createConsent({
              sharedWithName: doctor.name,
              sharedWithRole: 'DOCTOR',
              sharedWithNhcId: doctor.neoHealthCardId,
              // The scopes the server actually gates on.
              scopes: ['MEDICAL_HISTORY', 'PRESCRIPTIONS', 'LAB_REPORTS'],
              validHours: 72,
            });
          } catch {
            setConsentWarning('Booking succeeded, but the record-sharing consent could NOT be recorded — grant it from Consent & Sharing.');
          }
        }
      }

      setSuccess(true);
    } catch (err: any) {
      setSubmitError(err?.message || 'Could not complete booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedStep1 = !!consultationType;
  const canProceedStep2 = !!selectedSlot;
  const canProceedStep3 = reason.trim().length > 0;

  if (loading) return <Spinner />;

  if (!doctor) {
    return (
      <div style={{ background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.font, paddingBottom: 100 }}>
        <div style={{ textAlign: 'center', padding: 24 }}>
          <p style={{ color: T.textSec, fontSize: 14 }}>Unable to load doctor details.</p>
          <button onClick={() => navigate(-1)} style={{ background: T.teal, color: T.white, border: 'none', borderRadius: 10, padding: '10px 20px', fontFamily: T.font, fontWeight: 700, cursor: 'pointer', marginTop: 12 }}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <SuccessScreen
        doctorName={doctor.name}
        tokenNumber={successData.tokenNumber}
        date={getDateChipLabel(selectedDateOffset)}
        time={selectedSlot || ''}
        serviceType={consultTypeLabel[consultationType] || consultationType}
        consentWarning={consentWarning}
        onViewAppointments={() => navigate('/patient/appointments')}
        onGoToTimeline={() => navigate('/patient/timeline')}
        onBackToServices={() => navigate('/patient/care-services')}
      />
    );
  }

  return (
    <div
      style={{
        background: T.bg,
        fontFamily: T.font,
        margin: '0 auto',
        paddingBottom: 100,
      }}
    >
      {/* ── Step Indicator ───────────────────────────────────── */}
      <StepIndicator current={step} total={4} />

      {/* ── Doctor mini-card ─────────────────────────────────── */}
      <div style={{ padding: '12px 16px 0' }}>
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: T.navy + '10', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconStethoscope color={T.navy} />
          </div>
          <div>
            <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text }}>{doctor.name}</div>
            <div style={{ fontFamily: T.font, fontSize: 11, color: T.textSec }}>{doctor.specialties?.[0]} · {doctor.city}</div>
          </div>
        </div>
      </div>

      {/* ────────────────── STEP 1: SELECT SERVICE ─────────── */}
      {step === 1 && (
        <div style={{ padding: '20px 16px 0' }}>
          <h2 style={{ fontFamily: T.font, fontSize: 16, fontWeight: 800, color: T.text, margin: '0 0 4px' }}>
            Select Service
          </h2>
          <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: '0 0 18px', lineHeight: 1.6 }}>
            Choose how you'd like to consult with {doctor.name}.
          </p>

          <ServiceOption
            icon={<IconBuilding color={consultationType === 'clinic' ? T.navy : T.textTer} />}
            label="Clinic Visit"
            description="In-person consultation at the clinic"
            price={getPriceForType('clinic')}
            selected={consultationType === 'clinic'}
            onClick={() => setConsultationType('clinic')}
          />
          <ServiceOption
            icon={<IconVideo color={consultationType === 'telemedicine' ? T.navy : T.textTer} />}
            label="Telemedicine"
            description="Video / audio consultation from home"
            price={getPriceForType('telemedicine')}
            selected={consultationType === 'telemedicine'}
            onClick={() => setConsultationType('telemedicine')}
          />
          <ServiceOption
            icon={<IconRepeat color={consultationType === 'followup' ? T.navy : T.textTer} />}
            label="Follow-up"
            description="Revisit after a previous consultation"
            price={getPriceForType('followup')}
            selected={consultationType === 'followup'}
            onClick={() => setConsultationType('followup')}
          />
        </div>
      )}

      {/* ────────────────── STEP 2: DATE & TIME ─────────────── */}
      {step === 2 && (
        <div style={{ padding: '20px 16px 0' }}>
          <h2 style={{ fontFamily: T.font, fontSize: 16, fontWeight: 800, color: T.text, margin: '0 0 4px' }}>
            Select Date & Time
          </h2>
          <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: '0 0 18px', lineHeight: 1.6 }}>
            Pick a convenient slot for your {consultTypeLabel[consultationType]} appointment.
          </p>

          {/* Date chips */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.textSec, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Date</div>
            <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
              <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
                {[0, 1, 2, 3, 4].map((offset) => (
                  <button
                    key={offset}
                    onClick={() => { setSelectedDateOffset(offset); setSelectedSlot(null); }}
                    style={{
                      fontFamily: T.font,
                      fontSize: 12,
                      fontWeight: 600,
                      color: selectedDateOffset === offset ? T.white : T.textSec,
                      background: selectedDateOffset === offset ? T.navy : T.white,
                      border: `1.5px solid ${selectedDateOffset === offset ? T.navy : T.border}`,
                      borderRadius: 12,
                      padding: '9px 15px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s ease',
                      boxShadow: selectedDateOffset === offset ? `0 2px 8px ${T.navy}30` : T.shadow,
                    }}
                  >
                    {getDateChipLabel(offset)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Time slots — the doctor's own published openings when there are
              any, otherwise a stated preference. Never an invented grid. */}
          <div>
            <div style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.textSec, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {slots.hasSchedule ? 'Available times' : 'Preferred time'}
            </div>

            {slots.loading ? (
              <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec }}>Checking availability…</div>
            ) : slots.hasSchedule ? (
              (() => {
                const dayKey = getFullDate(selectedDateOffset);
                const open = slots.slotsFor(dayKey);
                if (open.length === 0) {
                  return (
                    <div style={{
                      fontFamily: T.font, fontSize: 12, color: T.textSec, background: T.bg,
                      border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px', lineHeight: 1.5,
                    }}>
                      No openings left on this date.
                      {slots.openDates.length > 0
                        ? ` This doctor still has openings on ${slots.openDates.slice(0, 3).map((d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })).join(', ')}.`
                        : ' Every published slot in the next two weeks is taken.'}
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {open.map((slot) => {
                      const isSelected = selectedSlotId === slot.id;
                      return (
                        <button
                          key={slot.id}
                          onClick={() => { setSelectedSlot(slot.startTime); setSelectedSlotId(slot.id); }}
                          style={{
                            fontFamily: T.font,
                            fontSize: 12,
                            fontWeight: 600,
                            color: isSelected ? T.white : T.navy,
                            background: isSelected ? T.teal : T.white,
                            border: `1.5px solid ${isSelected ? T.teal : T.navy + '30'}`,
                            borderRadius: 10,
                            padding: '9px 14px',
                            cursor: 'pointer',
                            transition: 'all 0.12s ease',
                            boxShadow: isSelected ? `0 2px 8px ${T.teal}35` : 'none',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                          }}
                        >
                          <span>{formatSlotTime(slot.startTime)}</span>
                          <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.75 }}>
                            {slot.remaining === 1 ? 'last seat' : `${slot.remaining} seats`}
                            {slot.clinicName ? ` · ${slot.clinicName}` : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <>
                <div style={{
                  fontFamily: T.font, fontSize: 11.5, color: T.textSec, lineHeight: 1.5,
                  background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10,
                  padding: '10px 12px', marginBottom: 10,
                }}>
                  {slots.error
                    ? `${slots.error} Pick the time you would prefer — the clinic confirms it.`
                    : 'This doctor does not publish a schedule here. Pick the time you would prefer; the clinic confirms the final time.'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {preferredTimeGrid().map((time) => {
                    const isSelected = selectedSlot === time && !selectedSlotId;
                    return (
                      <button
                        key={time}
                        onClick={() => { setSelectedSlot(time); setSelectedSlotId(null); }}
                        style={{
                          fontFamily: T.font,
                          fontSize: 12,
                          fontWeight: 600,
                          color: isSelected ? T.white : T.navy,
                          background: isSelected ? T.teal : T.white,
                          border: `1.5px solid ${isSelected ? T.teal : T.navy + '30'}`,
                          borderRadius: 10,
                          padding: '9px 14px',
                          cursor: 'pointer',
                          transition: 'all 0.12s ease',
                          boxShadow: isSelected ? `0 2px 8px ${T.teal}35` : 'none',
                        }}
                      >
                        {formatSlotTime(time)}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {selectedSlot && (
              <div
                style={{
                  marginTop: 14,
                  background: T.teal + '0D',
                  border: `1px solid ${T.teal}25`,
                  borderRadius: 10,
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <IconCheck color={T.teal} size={14} />
                <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.teal }}>
                  {selectedSlotId ? 'Booking' : 'Requesting'}: {getDateChipLabel(selectedDateOffset)}, {formatSlotTime(selectedSlot)}
                  {selectedSlotId ? '' : ' · to be confirmed by the clinic'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ────────────────── STEP 3: VISIT DETAILS ───────────── */}
      {step === 3 && (
        <div style={{ padding: '20px 16px 0' }}>
          <h2 style={{ fontFamily: T.font, fontSize: 16, fontWeight: 800, color: T.text, margin: '0 0 4px' }}>
            Visit Details
          </h2>
          <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: '0 0 20px', lineHeight: 1.6 }}>
            Help the doctor prepare for your visit.
          </p>

          {/* Reason for visit */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>
              Reason for Visit <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe your symptoms or reason for visiting..."
              rows={4}
              style={{
                width: '100%',
                fontFamily: T.font,
                fontSize: 13,
                color: T.text,
                background: T.white,
                border: `1.5px solid ${reason.trim() ? T.teal : T.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                outline: 'none',
                resize: 'none',
                boxSizing: 'border-box',
                lineHeight: 1.6,
                transition: 'border-color 0.15s ease',
              }}
            />
          </div>

          {/* Optional note */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>
              Additional Notes <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 400, color: T.textTer }}>(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Any allergies, medications, or additional information..."
              rows={3}
              style={{
                width: '100%',
                fontFamily: T.font,
                fontSize: 13,
                color: T.text,
                background: T.white,
                border: `1.5px solid ${T.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                outline: 'none',
                resize: 'none',
                boxSizing: 'border-box',
                lineHeight: 1.6,
              }}
            />
          </div>

          {/* Consent checkbox */}
          <button
            onClick={() => setShareRecords((v) => !v)}
            style={{
              width: '100%',
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              background: shareRecords ? T.teal + '08' : T.white,
              border: `1.5px solid ${shareRecords ? T.teal : T.border}`,
              borderRadius: 12,
              padding: '14px 16px',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease',
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: 6,
                border: `2px solid ${shareRecords ? T.teal : T.border}`,
                background: shareRecords ? T.teal : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 1,
                transition: 'all 0.15s ease',
              }}
            >
              {shareRecords && <IconCheck color={T.white} size={12} />}
            </div>
            <div>
              <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>
                Share health records with this provider
              </div>
              <div style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, lineHeight: 1.6 }}>
                Records a 72-hour consent grant for {doctor.name} on your NeoHealthCard account. You can revoke it anytime from Consent &amp; Sharing.
              </div>
            </div>
          </button>

          {shareRecords && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10, padding: '10px 12px', background: T.teal + '0A', border: `1px solid ${T.teal}20`, borderRadius: 10 }}>
              <IconShield color={T.teal} />
              <span style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, lineHeight: 1.6 }}>
                A consent grant (health summary, prescriptions, lab reports · 72h) will be created when you confirm this booking.
              </span>
            </div>
          )}
        </div>
      )}

      {/* ────────────────── STEP 4: CONFIRM & PAY ───────────── */}
      {step === 4 && (
        <div style={{ padding: '20px 16px 0' }}>
          <h2 style={{ fontFamily: T.font, fontSize: 16, fontWeight: 800, color: T.text, margin: '0 0 4px' }}>
            Confirm & Pay
          </h2>
          <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: '0 0 20px', lineHeight: 1.6 }}>
            Review your booking details before confirming.
          </p>

          {/* Summary card */}
          <div
            style={{
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: 16,
              padding: '18px',
              marginBottom: 20,
              boxShadow: T.shadow,
            }}
          >
            <div style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.textTer, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Booking Summary
            </div>

            {[
              { label: 'Doctor', value: doctor.name },
              { label: 'Service', value: consultTypeLabel[consultationType] || consultationType },
              { label: 'Date', value: getDateChipLabel(selectedDateOffset) },
              { label: 'Time', value: selectedSlot || '—' },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 12, marginBottom: 12, borderBottom: `1px solid ${T.border}` }}>
                <span style={{ fontFamily: T.font, fontSize: 12, color: T.textSec }}>{label}</span>
                <span style={{ fontFamily: T.font, fontSize: 13, fontWeight: 600, color: T.text, textAlign: 'right', maxWidth: 200 }}>{value}</span>
              </div>
            ))}

            {/* Fee */}
            {getPriceForType(consultationType) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text }}>Consultation Fee</span>
                <span style={{ fontFamily: T.font, fontSize: 18, fontWeight: 800, color: T.navy }}>{getPriceForType(consultationType)}</span>
              </div>
            )}
          </div>

          {/* Payment — honest: no online payment exists, fee is paid at the clinic */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>Payment</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', background: T.ice, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px' }}>
              <IconCash color={T.navy} />
              <div>
                <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text }}>Pay at the clinic</div>
                <div style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, marginTop: 2 }}>
                  Online payment is not available yet — the fee is collected at your visit.
                </div>
              </div>
            </div>
          </div>

          {/* Error */}
          {submitError && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
              <span style={{ fontFamily: T.font, fontSize: 13, color: '#DC2626' }}>{submitError}</span>
            </div>
          )}

          {/* Reason preview */}
          {reason && (
            <div
              style={{
                background: T.ice,
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 16,
              }}
            >
              <div style={{ fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.textSec, marginBottom: 5 }}>Reason for Visit</div>
              <div style={{ fontFamily: T.font, fontSize: 13, color: T.text, lineHeight: 1.6 }}>{reason}</div>
            </div>
          )}

          {/* Safety note */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '12px 14px', background: T.navy + '05', border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 8 }}>
            <IconShield color={T.textTer} />
            <span style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, lineHeight: 1.6 }}>
              Your records are shared only if you granted consent above — revocable anytime from Consent &amp; Sharing.
            </span>
          </div>
        </div>
      )}

      {/* ── Bottom Action Bar ─────────────────────────────────── */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: T.white,
          borderTop: `1px solid ${T.border}`,
          padding: '14px 16px 20px',
          zIndex: 30,
          boxShadow: '0 -4px 16px rgba(11,37,69,0.08)',
        }}
      >
        {step < 4 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={
              (step === 1 && !canProceedStep1) ||
              (step === 2 && !canProceedStep2) ||
              (step === 3 && !canProceedStep3)
            }
            style={{
              width: '100%',
              fontFamily: T.font,
              fontSize: 14,
              fontWeight: 700,
              color: T.white,
              background:
                (step === 1 && !canProceedStep1) || (step === 2 && !canProceedStep2) || (step === 3 && !canProceedStep3)
                  ? T.border
                  : `linear-gradient(135deg, ${T.teal} 0%, #009B9B 100%)`,
              border: 'none',
              borderRadius: 12,
              padding: '14px 0',
              cursor:
                (step === 1 && !canProceedStep1) || (step === 2 && !canProceedStep2) || (step === 3 && !canProceedStep3)
                  ? 'not-allowed'
                  : 'pointer',
              boxShadow:
                (step === 1 && !canProceedStep1) || (step === 2 && !canProceedStep2) || (step === 3 && !canProceedStep3)
                  ? 'none'
                  : `0 3px 12px ${T.teal}35`,
              transition: 'background 0.2s ease, box-shadow 0.2s ease',
            }}
          >
            Continue
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              width: '100%',
              fontFamily: T.font,
              fontSize: 14,
              fontWeight: 700,
              color: T.white,
              background: submitting ? T.border : `linear-gradient(135deg, ${T.navy} 0%, #003670 100%)`,
              border: 'none',
              borderRadius: 12,
              padding: '14px 0',
              cursor: submitting ? 'not-allowed' : 'pointer',
              boxShadow: submitting ? 'none' : `0 3px 12px ${T.navy}30`,
              transition: 'background 0.2s ease',
            }}
          >
            {submitting ? 'Confirming...' : 'Confirm Booking'}
          </button>
        )}
      </div>
    </div>
  );
}
